import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";
import Logo from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("numedia_admin_token")) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      if (!data?.token) {
        throw new Error("Missing token in server response");
      }
      localStorage.setItem("numedia_admin_token", data.token);
      // Clean up legacy password entry if present
      localStorage.removeItem("numedia_admin_pw");
      navigate("/admin", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 401
          ? "Mot de passe invalide."
          : "Erreur de connexion au serveur. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background:
          "radial-gradient(ellipse at top left, rgba(99,102,241,0.15), transparent 50%), radial-gradient(ellipse at bottom right, rgba(236,72,153,0.12), transparent 50%), #0B1027",
      }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={16} />
          Retour au site
        </Link>

        <div className="bg-white rounded-3xl shadow-tactile-lg p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Logo size={36} />
            <div>
              <div className="font-brand text-lg text-[#0B1027] font-medium tracking-tight uppercase">
                Numedia
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-aurora font-bold mt-0.5">
                Cockpit admin
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0B1027] tracking-tight leading-tight">
            Connexion{" "}
            <em className="not-italic italic font-light text-aurora">
              administrateur.
            </em>
          </h1>
          <p className="text-[#0F172A]/70 mt-3 text-sm leading-relaxed">
            Espace privé · accès aux réservations et demandes clients.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <label
                htmlFor="admin-password"
                className="text-[11px] uppercase tracking-[0.18em] text-[#0F172A]/60 font-bold"
              >
                Mot de passe
              </label>
              <div className="relative mt-2">
                <Lock
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F172A]/40"
                />
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-14 pl-11 pr-4 rounded-2xl border border-[#0B1027]/15 bg-white text-[#0B1027] focus:outline-none focus:border-[#A855F7] transition-colors"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              data-testid="admin-login-submit"
              className="w-full btn-aurora text-white h-14 rounded-full font-semibold shadow-tactile-lg hover-lift transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Connexion…
                </>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          Numedia · Studio indépendant · Bruxelles
        </p>
      </div>
    </main>
  );
}
